import { Button } from "@tracify/ui/button";
import {
  PatchAdAccountCsidsInput,
  usePatchAdAccountCsids,
} from "@lib/api-hooks/useAdAccounts/usePatchAdAccountsCsids";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { Spinner } from "@tracify/ui/spinner";
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@tracify/ui/dialog";
import { Stepper } from "../Presets/Stepper";
import { Checkbox } from "@tracify/ui/checkbox";
import { Label } from "@tracify/ui/label";
import { MultiSelect } from "@tracify/ui/multi-select";
import { useConnectedAdAccounts } from "@lib/api-hooks/useAdAccounts/useConnectedAdAccounts";
import { toast } from "sonner";
import { useRouter, useSearchParams } from "next/navigation";
import { queryParamsToPurge } from "@components/LinkWithQuery/LinkWithQuery";
import { useUnmappedAccountsWarning } from "./UnmappedAccountsWarningDialog";
import { SafetyDialog } from "@tracify/ui/safety-dialog";
import {
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { CONSTANT_LABELS } from "@/constants/constants";
import {
  useCheckConnections,
  CheckConnectionsResponse,
} from "@lib/api-hooks/useAdAccounts/useCheckConnections";
import {
  buildPathWithSearchParams,
  cloneSearchParams,
  deleteSearchParams,
} from "@lib/navigation/url-search-params";

type Props = {
  onClose: () => void;
  open: boolean;
  type:
    | "google"
    | "facebook"
    | "tiktok"
    | "pinterest"
    | "outbrain"
    | "msads"
    | "taboola";
  includeAll?: boolean;
  enableAddNewAccount?: boolean;
};

const AssignAdAccountModal = ({
  onClose,
  open,
  type,
  includeAll = true,
}: Props) => {
  const {
    stores,
    selectedOrganisationData,
    isPending: isPendingSelectedStores,
  } = useSelectedStores();
  const {
    data: accounts,
    isPending,
    isFetching,
  } = useConnectedAdAccounts({
    organisationId: selectedOrganisationData?.id ?? "",
    includeAll: includeAll,
  });
  const connectedAdAccounts = accounts?.accounts;
  const customerSites = selectedOrganisationData?.customerSites.map(
    (el) => el.id
  );

  const [currentStep, setCurrentStep] = useState(0);
  // Step 0: account selection state (defaults to all accounts in channel)
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [connectionSuccessful, setConnectionSuccessful] = useState(false);
  const [connectionResults, setConnectionResults] =
    useState<CheckConnectionsResponse | null>(null);
  const [hasTestedConnections, setHasTestedConnections] = useState(false);

  const { showUnmappedAccountsWarning } = useUnmappedAccountsWarning();
  const checkConnectionsMutation = useCheckConnections();

  const allAdAccounts = useMemo(() => {
    return Object.values(connectedAdAccounts ?? {}).flat();
  }, [connectedAdAccounts]);

  const adAccountsForChannel = useMemo(() => {
    return (
      connectedAdAccounts?.[type as keyof typeof connectedAdAccounts] ?? []
    );
  }, [connectedAdAccounts, type]);

  const adAccountIdsForChannel = useMemo(() => {
    return adAccountsForChannel.map((account) => account.account_id);
  }, [adAccountsForChannel]);

  const selectedAccountIdsForChannel = useMemo(() => {
    const channelAccountIdSet = new Set(adAccountIdsForChannel);
    return selectedAccountIds.filter((accountId) =>
      channelAccountIdSet.has(accountId)
    );
  }, [adAccountIdsForChannel, selectedAccountIds]);

  const areAllAccountsSelected =
    adAccountIdsForChannel.length > 0 &&
    selectedAccountIdsForChannel.length === adAccountIdsForChannel.length;

  const bulkSelectionLabel = areAllAccountsSelected
    ? "Deselect all"
    : "Select all";

  // Only show the selected accounts in following steps
  const selectedAccounts = useMemo(() => {
    if (!selectedAccountIds?.length) return adAccountsForChannel;
    const idSet = new Set(selectedAccountIds);
    return adAccountsForChannel.filter((a) => idSet.has(a.account_id));
  }, [adAccountsForChannel, selectedAccountIds]);

  const router = useRouter();
  const searchParams = useSearchParams();
  const [accountToStoresMapping, setAccountToStoresMapping] = useState<{
    [key: string]: string[];
  }>(() => {
    return (
      Object.values(connectedAdAccounts ?? {})
        .flat()
        ?.reduce(
          (acc, account) => {
            acc[account.account_id] = account.connected_stores.map(
              (store) => store.csid
            );
            return acc;
          },
          {} as Record<string, string[]>
        ) ?? {}
    );
  });

  // Track original mapping to detect changes
  const [originalMapping, setOriginalMapping] = useState<{
    [key: string]: string[];
  }>({});

  const updateAdAccountsMutation = usePatchAdAccountCsids();
  const organisationStores = stores?.filter((el) =>
    customerSites?.includes(el.csid)
  );

  // Check for unmapped accounts to determine if we need an additional step
  const unmappedAccountsData = useMemo(() => {
    return showUnmappedAccountsWarning(
      selectedAccounts,
      accountToStoresMapping,
      type,
      () => {}
    );
  }, [
    selectedAccounts,
    accountToStoresMapping,
    type,
    showUnmappedAccountsWarning,
  ]);

  // Calculate steps with new selection step (index 0)
  // Steps: 0 = Select accounts, 1..N = mapping for selected accounts, then optional unmapped warning, then test
  const mappingStartIndex = 1;
  const mappingEndIndex = mappingStartIndex + selectedAccounts.length - 1; // inclusive, only if any selected
  const unmappedStepIndex = mappingEndIndex + 1; // position after last mapping step
  const testStepIndex = unmappedAccountsData.hasUnmappedAccounts
    ? unmappedStepIndex + 1
    : unmappedStepIndex; // if no unmapped step, test directly follows mappings
  const totalSteps = testStepIndex + 1;
  const isUnmappedWarningStep =
    currentStep === unmappedStepIndex &&
    unmappedAccountsData.hasUnmappedAccounts;
  const isTestConnectionsStep = currentStep === testStepIndex;

  // Detect if there are unsaved changes - always true unless connection was successful
  const hasUnsavedChanges = useMemo(() => {
    if (connectionSuccessful) return false;
    if (!open) return false;
    return true;
  }, [connectionSuccessful, open]);

  // State for manually controlling the safety dialog
  const [showSafetyDialog, setShowSafetyDialog] = useState(false);

  const handleSafetyLeave = () => {
    // Reset to original state when leaving
    setAccountToStoresMapping(originalMapping);
    setCurrentStep(0);
    setConnectionSuccessful(false);
    setConnectionResults(null);
    setHasTestedConnections(false);
    setShowSafetyDialog(false);
    onClose();
  };

  const handleSafetyStay = () => {
    setShowSafetyDialog(false);
  };

  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (initialized) return;
    if (isFetching) return;
    if (isPendingSelectedStores) return;
    if (!selectedOrganisationData?.id) return;

    setInitialized(true);
    const mapping =
      allAdAccounts.reduce(
        (acc, account) => {
          acc[account.account_id] = account.connected_stores.map(
            (store) => store.csid
          );
          return acc;
        },
        {} as Record<string, string[]>
      ) ?? {};
    setAccountToStoresMapping(mapping);
    setOriginalMapping(mapping);
    setCurrentStep(0);
    // Default selection to all accounts for the current channel
    setSelectedAccountIds(
      (adAccountsForChannel ?? []).map((a) => a.account_id)
    );
    setConnectionSuccessful(false);
    setConnectionResults(null);
    setHasTestedConnections(false);
  }, [
    allAdAccounts,
    initialized,
    isPending,
    selectedOrganisationData?.id,
    isPendingSelectedStores,
    open,
    adAccountsForChannel,
    isFetching,
  ]);

  const handleSaveAndContinue = () => {
    // Handle selection step
    if (currentStep === 0) {
      // Move into first mapping step
      setCurrentStep(mappingStartIndex);
      return;
    }

    // In mapping steps range
    if (currentStep >= mappingStartIndex && currentStep <= mappingEndIndex) {
      if (currentStep < mappingEndIndex) {
        setCurrentStep(currentStep + 1);
      } else if (
        currentStep === mappingEndIndex &&
        unmappedAccountsData.hasUnmappedAccounts
      ) {
        setCurrentStep(unmappedStepIndex);
      } else {
        // Last mapping step and no unmapped step -> save now
        handleSaveMappings();
      }
      return;
    }

    if (isUnmappedWarningStep) {
      // Save mappings and then move to test connections step
      handleSaveMappings();
      return;
    }
  };

  const handleSaveMappings = async () => {
    try {
      const mappings: PatchAdAccountCsidsInput["mappings"] = [];
      const selectedIdSet = new Set(selectedAccountIds);
      for (const [adAccountId, csids] of Object.entries(
        accountToStoresMapping
      )) {
        // Restrict to only selected accounts in the current channel
        if (!selectedIdSet.has(adAccountId)) continue;
        const adAccountInChannel = adAccountsForChannel.find(
          (acc) => acc.account_id === adAccountId
        );
        if (!adAccountInChannel) {
          console.warn(
            `Ad account ${adAccountId} not found in current channel's accounts for mutation.`
          );
          continue;
        }
        mappings.push({
          source: adAccountInChannel.source,
          account_id: adAccountInChannel.account_id,
          associated_csids: csids,
        });
      }
      if (
        mappings.length === 0 &&
        Object.keys(accountToStoresMapping).length > 0
      ) {
        toast.error(
          "No valid accounts found to update. Please check mappings."
        );
        return;
      }
      await updateAdAccountsMutation.mutateAsync({ mappings });

      // Update original mapping to reflect saved changes
      setOriginalMapping({ ...accountToStoresMapping });

      // Move to test connections step after successful save
      setCurrentStep(currentStep + 1);
    } catch (error) {
      toast.error(
        (error as any)?.message ??
          "An error occurred while saving account mappings."
      );
    }
  };

  const handleCompleteSetup = async () => {
    // Mark connection as successful before navigating
    setConnectionSuccessful(true);

    const params = deleteSearchParams(
      cloneSearchParams(searchParams),
      queryParamsToPurge
    );
    params.set("mapping_success", type);
    router.push(
      buildPathWithSearchParams(
        `/settings/integrations/${type === "facebook" ? "meta" : type}`,
        params
      )
    );
    onClose();
  };

  const noAccounts = adAccountsForChannel?.length === 0 && !isPending;

  const handleClose = () => {
    if (hasUnsavedChanges) {
      // Show the safety dialog instead of closing
      setShowSafetyDialog(true);
      return;
    }

    setAccountToStoresMapping({});
    setOriginalMapping({});
    setInitialized(false);
    setCurrentStep(0);
    setSelectedAccountIds([]);
    setConnectionSuccessful(false);
    setConnectionResults(null);
    setHasTestedConnections(false);
    onClose();
  };

  // Current account to map from the selected subset
  const currentAccountToMap =
    currentStep >= mappingStartIndex && currentStep <= mappingEndIndex
      ? selectedAccounts?.[currentStep - mappingStartIndex]
      : undefined;

  const stepperSteps = useMemo(() => {
    return ["Select ad accounts", "Assign ad accounts", "Test connections"];
  }, []);

  const stepperCurrentStep = useMemo(() => {
    if (currentStep === 0) return 1;
    if (isTestConnectionsStep) return 3;
    return 2;
  }, [currentStep, isTestConnectionsStep]);

  const handleBack = () => {
    // Reset connection results when navigating away from test step
    if (isTestConnectionsStep) {
      setConnectionResults(null);
      setHasTestedConnections(false);
    }
    setCurrentStep(currentStep - 1);
  };

  // Helper function to go back to specific account step from unmapped warning step
  const handleBackToAccount = (accountIndex: number) => {
    // Reset connection results when navigating away from later steps
    setConnectionResults(null);
    setHasTestedConnections(false);
    // accountIndex refers to index within selectedAccounts
    setCurrentStep(mappingStartIndex + accountIndex);
  };

  const handleTestConnections = async () => {
    if (!selectedOrganisationData?.id) return;

    try {
      const result = await checkConnectionsMutation.mutateAsync({
        organisation_id: selectedOrganisationData.id,
        source: type,
      });
      setConnectionResults(result);
      setHasTestedConnections(true);

      const invalidCount = result.filter((r) => !r.is_valid).length;
      if (invalidCount > 0) {
        toast.error(`${invalidCount} account(s) have invalid connections`);
      } else {
        toast.success("All accounts are connected successfully");
      }
    } catch (error) {
      toast.error(
        (error as any)?.message ??
          "An error occurred while testing connections."
      );
    }
  };

  const getConnectionStatus = (accountId: string) => {
    if (!connectionResults) return null;
    return connectionResults.find((r) => r.account_id === accountId);
  };

  return (
    <>
      <SafetyDialog
        open={showSafetyDialog}
        onOpenChange={() => {}}
        onLeave={handleSafetyLeave}
        onStay={handleSafetyStay}
      />
      <Dialog
        open={open}
        onOpenChange={(open) => {
          if (!open) {
            handleClose();
          }
        }}
      >
        <DialogContent className="max-w-3xl w-full max-h-[80vh] overflow-y-auto">
          <DialogHeader className="sr-only">
            <DialogTitle>Account Assignment</DialogTitle>
          </DialogHeader>

          {!initialized ? (
            <div className="w-full flex flex-col items-center justify-center gap-4 p-4 bg-muted rounded-lg">
              <Spinner />
              <p className="">Loading Ad account data</p>
            </div>
          ) : noAccounts ? (
            <div className="w-full flex flex-col items-center justify-center gap-4 p-4 bg-muted rounded-lg">
              <p className="">
                No ad accounts found or existing ad accounts need to be
                re-connected.
              </p>
            </div>
          ) : (
            <>
              {adAccountsForChannel && adAccountsForChannel.length > 0 ? (
                <>
                  <Stepper
                    steps={stepperSteps}
                    currentStep={stepperCurrentStep}
                    onStepClick={() => {}}
                  />

                  {isUnmappedWarningStep ? (
                    // Unmapped accounts warning step
                    <div className="">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900">
                          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold">
                            Unmapped Ad Accounts
                          </h2>
                          <p className="text-sm text-muted-foreground mt-1">
                            Some {CONSTANT_LABELS[type] ?? type} ad accounts are
                            not mapped to any stores.
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <p className="text-sm">
                          The following ad accounts have no store mappings:
                        </p>
                        <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                          {unmappedAccountsData.unmappedAccounts.map(
                            (account) => (
                              <div
                                key={account.account_id}
                                className="rounded-lg bg-yellow-100 dark:bg-yellow-900/20 p-3 border border-yellow-400 dark:border-yellow-800"
                              >
                                <div className="flex justify-between items-start">
                                  <div>
                                    <div className="font-medium text-sm">
                                      {account.name || "Unnamed Account"}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      ID: {account.account_id}
                                    </div>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const accountIndex =
                                        selectedAccounts.findIndex(
                                          (acc) =>
                                            acc.account_id ===
                                            account.account_id
                                        );
                                      if (accountIndex !== -1) {
                                        handleBackToAccount(accountIndex);
                                      }
                                    }}
                                  >
                                    Map Account
                                  </Button>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Data from unmapped accounts will not be included in
                          your analytics and will not be visible in the
                          dashboard.
                        </p>
                      </div>
                    </div>
                  ) : isTestConnectionsStep ? (
                    // Test connections step
                    <div className="">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                          <ShieldCheckIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold">
                            Test Account Connections
                          </h2>
                          <p className="text-sm text-muted-foreground mt-1">
                            Your account mappings have been saved. Now verify
                            that your {CONSTANT_LABELS[type] ?? type} accounts
                            are properly connected.
                          </p>
                        </div>
                      </div>

                      {!hasTestedConnections ? (
                        <div className="space-y-4">
                          <p className="text-sm">
                            We recommend testing your account connections to
                            ensure data flows correctly. This will verify that
                            Tracify can access your ad account data.
                          </p>
                          <Button
                            onClick={handleTestConnections}
                            loading={checkConnectionsMutation.isPending}
                            disabled={checkConnectionsMutation.isPending}
                          >
                            {checkConnectionsMutation.isPending ? (
                              <Spinner className="mr-2 h-4 w-4" />
                            ) : (
                              <ShieldCheckIcon className="h-4 w-4 mr-2" />
                            )}
                            Test Connections
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="space-y-3 max-h-[40vh] overflow-y-auto">
                            {selectedAccounts.map((account) => {
                              const connectionStatus = getConnectionStatus(
                                account.account_id
                              );
                              return (
                                <div
                                  key={account.account_id}
                                  className={`rounded-lg p-3 border ${
                                    connectionStatus?.is_valid
                                      ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                                      : "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
                                  }`}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="font-medium text-sm">
                                        {account.name || "Unnamed Account"}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        ID: {account.account_id}
                                      </div>
                                      {connectionStatus?.is_valid ? (
                                        <div className="text-xs text-green-700 dark:text-green-300 mt-1">
                                          ✓ Connection is valid
                                        </div>
                                      ) : (
                                        <div className="text-xs text-red-700 dark:text-red-300 mt-1">
                                          ✗ Connection is invalid. Reconnect it
                                          or contact support and provide the
                                          following reference ID.
                                          {connectionStatus?.reference_id && (
                                            <div className="font-semibold font-mono mt-1">
                                              Reference ID:{" "}
                                              {connectionStatus.reference_id}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <Button
                            variant="outline"
                            onClick={handleTestConnections}
                            loading={checkConnectionsMutation.isPending}
                            disabled={checkConnectionsMutation.isPending}
                          >
                            <ShieldCheckIcon className="h-4 w-4 mr-2" />
                            Test Again
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : currentStep === 0 ? (
                    // Selection step (step 0)
                    <div className="">
                      <h2 className="text-xl font-semibold">
                        Select ad accounts to assign
                      </h2>
                      <p className="text-sm text-muted-foreground mt-2">
                        Choose which ad accounts you want to connect to your
                        store(s).
                      </p>
                      <div className="mt-4 flex justify-end gap-2">
                        <Button
                          type="button"
                          variant={"ghost"}
                          size="sm"
                          onClick={() =>
                            setSelectedAccountIds(
                              areAllAccountsSelected
                                ? []
                                : adAccountIdsForChannel
                            )
                          }
                          disabled={adAccountIdsForChannel.length === 0}
                        >
                          {bulkSelectionLabel}
                        </Button>
                      </div>
                      <div className="mt-4 rounded-lg border bg-muted/30 p-3 max-h-[40vh] overflow-y-auto">
                        {adAccountsForChannel?.length ? (
                          adAccountsForChannel.map((account) => {
                            const checked = selectedAccountIds.includes(
                              account.account_id
                            );
                            const mappedCount =
                              originalMapping?.[account.account_id]?.length ||
                              0;
                            return (
                              <div
                                key={account.account_id}
                                className="flex items-center justify-between py-2"
                              >
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`sel-${account.account_id}`}
                                    checked={checked}
                                    onCheckedChange={(value) => {
                                      const isChecked = Boolean(value);
                                      setSelectedAccountIds((prev) => {
                                        if (isChecked) {
                                          if (prev.includes(account.account_id))
                                            return prev;
                                          return [...prev, account.account_id];
                                        }
                                        return prev.filter(
                                          (id) => id !== account.account_id
                                        );
                                      });
                                    }}
                                  />
                                  <Label
                                    htmlFor={`sel-${account.account_id}`}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  >
                                    {account.name || account.account_id}
                                  </Label>
                                </div>
                                {mappedCount > 0 ? (
                                  <div className="ml-4 inline-flex items-center gap-2">
                                    <span className="inline-flex items-center gap-1 rounded-lg bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 text-xs">
                                      <CheckCircleIcon className="h-3.5 w-3.5" />
                                      Mapped
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {mappedCount} store
                                      {mappedCount > 1 ? "s" : ""}
                                    </span>
                                  </div>
                                ) : null}
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No ad accounts available.
                          </p>
                        )}
                      </div>
                    </div>
                  ) : currentAccountToMap ? (
                    // Regular account mapping step
                    <>
                      <div className="">
                        <h2 className="text-xl font-semibold">
                          Assign ad account (
                          {currentStep - mappingStartIndex + 1} of{" "}
                          {selectedAccounts.length})
                        </h2>
                        <p className="text-sm font-medium mt-1">
                          {currentAccountToMap.name || "Unnamed Account"} (
                          {currentAccountToMap.account_id})
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Select the store(s) where you want this ad
                          account&apos;s data to appear.
                        </p>
                      </div>

                      <div className="p-4 rounded-lg border bg-muted/30">
                        {organisationStores && organisationStores.length > 0 ? (
                          <MultiSelect
                            options={organisationStores.map((store) => ({
                              label: store.description,
                              value: store.csid,
                            }))}
                            value={
                              accountToStoresMapping[
                                currentAccountToMap.account_id
                              ] ?? []
                            }
                            onValueChange={(value) => {
                              setAccountToStoresMapping((prev) => ({
                                ...prev,
                                [currentAccountToMap.account_id]: value,
                              }));
                            }}
                            placeholder="Select store(s) for this account"
                            label="Stores"
                            className="p-3 w-full"
                            variant="alternate"
                          />
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No stores available for mapping.
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground py-4">
                      Loading account data or no accounts to map for this
                      channel.
                    </p>
                  )}

                  <DialogFooter className="mt-4">
                    {currentStep > 0 && (
                      <Button variant="ghost" onClick={handleBack}>
                        Back
                      </Button>
                    )}
                    {currentStep === 0 ? (
                      <Button
                        onClick={handleSaveAndContinue}
                        disabled={selectedAccountIds.length === 0}
                      >
                        Continue
                      </Button>
                    ) : isUnmappedWarningStep ? (
                      <Button
                        loading={updateAdAccountsMutation.isPending}
                        onClick={handleSaveAndContinue}
                        disabled={updateAdAccountsMutation.isPending}
                      >
                        {updateAdAccountsMutation.isPending ? (
                          <Spinner className="mr-2 h-4 w-4" />
                        ) : null}
                        Continue to Test
                      </Button>
                    ) : isTestConnectionsStep ? (
                      <Button onClick={handleCompleteSetup}>
                        Complete Setup
                      </Button>
                    ) : (
                      <Button
                        loading={updateAdAccountsMutation.isPending}
                        onClick={handleSaveAndContinue}
                        disabled={
                          updateAdAccountsMutation.isPending ||
                          !currentAccountToMap
                        }
                      >
                        {updateAdAccountsMutation.isPending ? (
                          <Spinner className="mr-2 h-4 w-4" />
                        ) : null}
                        Save and continue
                      </Button>
                    )}
                  </DialogFooter>
                </>
              ) : (
                <p className="text-sm text-muted-foreground py-4">
                  Loading account data or no accounts to map for this channel.
                </p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssignAdAccountModal;
